import { Link } from 'react-router-dom';
import UrgencyDot from '@/components/shared/UrgencyDot.jsx';
import CarrierBadge from './CarrierBadge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { cn } from '@/lib/utils.js';
import { formatDate } from '@/lib/date-display.js';

// Command Center deal card (redesign locked 2026-06-15;
// design-reference/command-center.mockup.html + CommandCenterSection.jsx).
// Spine = who / what / when / value:
//   who   — named insured (bold) + 4-level urgency dot
//   what  — aircraft: mono tail + make/model
//   when  — deadline date / PAST DUE (accent), top-right
//   value — premium (mono); 'exp' suffix pre-quote; one action button beside it
// Per-carrier badges (shared state vocab) + readiness gaps as dashed chips.
// Dropped from the prior card: the ND-APP number / meta_subline and the
// separate "Missing:" line (gaps read as dashed chips).

const money = (n) => (n == null || !Number.isFinite(Number(n)) ? null : '$' + Number(n).toLocaleString('en-US'));

function whenLabel(item) {
  const d = item.days_remaining;
  if (d == null) return null;
  if (d < 0) return { text: 'PAST DUE', past: true };
  if (!item.deadline_date) return { text: `${d}d`, past: false };
  return { text: formatDate(item.deadline_date), past: false };
}

export default function DealCard({ item }) {
  const target = `/account/${encodeURIComponent(item.account_id || item.deal_id)}/deal/${encodeURIComponent(item.deal_id)}`;
  const when = whenLabel(item);
  const carriers = item.carriers || [];
  const signals = item.readiness_signals || [];
  const val = money(item.value);
  const action = (item.actions || [])[0] || null;

  return (
    <Link
      to={target}
      className="block rounded-card border border-border bg-surface p-3 transition-colors hover:border-border-strong active:bg-border/20"
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <UrgencyDot urgency={item.urgency} />
          <span className="truncate text-sm font-semibold text-text">{item.name || '—'}</span>
        </div>
        {when ? (
          <span className={cn('shrink-0 text-xs font-medium', when.past ? 'text-accent' : 'text-text-muted')}>
            {when.text}
          </span>
        ) : null}
      </div>

      {item.aircraft?.tail || item.aircraft?.model ? (
        <div className="mt-1 truncate text-[11px] text-text-dim">
          {item.aircraft?.tail ? <span className="font-mono text-text-muted">{item.aircraft.tail}</span> : null}
          {item.aircraft?.model ? <>{item.aircraft?.tail ? ' · ' : ''}{item.aircraft.model}</> : null}
        </div>
      ) : null}

      {carriers.length > 0 ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {carriers.map((c) => <CarrierBadge key={`${c.carrier_slug}-${c.state}`} carrier={c} />)}
        </div>
      ) : null}

      {signals.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {signals.map((s) => (
            <span
              key={s.slug}
              className="inline-flex items-center rounded-full border border-dashed border-text-dim/50 px-2 py-0.5 text-[10px] text-text-dim"
            >
              {s.label}
            </span>
          ))}
        </div>
      ) : null}

      {(val || action) ? (
        <div className="mt-2.5 flex items-center justify-between gap-2" data-interactive onClick={(e) => e.stopPropagation()}>
          <span className="font-mono text-[13px] text-text-muted">
            {val || ''}
            {val && item.value_basis === 'expiring' ? <span className="ml-0.5 text-[10px] text-text-dim">exp</span> : null}
          </span>
          {action ? (
            <Button size="xs" variant={action.emphasis ? 'accent' : 'outline'}>{action.label}</Button>
          ) : <span />}
        </div>
      ) : null}
    </Link>
  );
}
